/** Editable animation sequences over a regular spritesheet grid. Pixels are never changed. */
export interface FrameCell { column: number; row: number }
export interface FrameMapping {
    version: 1
    cellWidth: number
    cellHeight: number
    animations: Record<string, FrameCell[]>
}
export interface SpriteAtlas {
    frames: Record<string, any>
    animations: Record<string, string[]>
    meta: any
}
export const gridForAtlas = (atlas: SpriteAtlas) => {
    const frames = Object.values(atlas.frames)
    const first = frames[0]
    if (!first) throw new Error("This atlas has no frame geometry to edit")
    const { w, h } = first.frame
    if (!Number.isInteger(w) || !Number.isInteger(h) || w < 1 || h < 1 || frames.some(f => f.rotated || f.trimmed || f.frame.w !== w || f.frame.h !== h || f.frame.x % w || f.frame.y % h)) throw new Error("Frame selection requires an untrimmed regular grid")
    return { cellWidth: w, cellHeight: h, columns: Math.floor(atlas.meta.size.w / w), rows: Math.floor(atlas.meta.size.h / h) }
}
/** Authored playback order survives catalog JSON key sorting, including repeated frames. */
export const animationFrameNames = (atlas: Pick<SpriteAtlas, "frames" | "animations">, animation: string): string[] =>
    atlas.animations?.[animation] ?? Object.keys(atlas.frames).filter(name => name.startsWith(`${animation}-`))
        .sort((a, b) => a.localeCompare(b, "en", { numeric: true }))

export const cellsForAnimation = (atlas: SpriteAtlas, animation: string): FrameCell[] => {
    const grid = gridForAtlas(atlas)
    return animationFrameNames(atlas, animation).filter(name => atlas.frames[name]).map(name => {
        const frame = atlas.frames[name].frame
        return { column: frame.x / grid.cellWidth, row: frame.y / grid.cellHeight }
    })
}
export const validateFrameMapping = (atlas: SpriteAtlas, mapping: FrameMapping) => {
    const grid = gridForAtlas(atlas)
    if (!mapping || mapping.version !== 1 || mapping.cellWidth !== grid.cellWidth || mapping.cellHeight !== grid.cellHeight || !mapping.animations || typeof mapping.animations !== "object" || Array.isArray(mapping.animations)) throw new Error("Frame mapping does not match this atlas grid")
    for (const [animation, cells] of Object.entries(mapping.animations)) {
        if (!/^(idle|walk|slash|thrust|cast|shoot|hurt|activate|deactivate|active|inactive)(-(back|left|front|right))?$/.test(animation)) throw new Error("Invalid animation name")
        if (!Array.isArray(cells) || cells.length > 256) throw new Error("An animation may contain up to 256 frames")
        for (const cell of cells) if (!cell || !Number.isInteger(cell.column) || !Number.isInteger(cell.row) || cell.column < 0 || cell.row < 0 || cell.column >= grid.columns || cell.row >= grid.rows) throw new Error("Selected frame is outside the spritesheet grid")
    }
}
export const applyFrameMapping = (atlas: SpriteAtlas, mapping: FrameMapping, namespace: string): SpriteAtlas => {
    validateFrameMapping(atlas, mapping)
    const frames = { ...atlas.frames }, animations = { ...atlas.animations }
    // Stable unique sheet suffix avoids PIXI's global frame-name collisions.
    let hash = 0
    for (const c of namespace) hash = ((hash << 5) - hash + c.charCodeAt(0)) | 0
    for (const [animation, cells] of Object.entries(mapping.animations)) {
        const existing = Object.entries(atlas.frames).filter(([name]) => name.startsWith(`${animation}-`))
        const template = existing[0]?.[1] || Object.values(atlas.frames)[0]
        for (const [name] of existing) delete frames[name]
        animations[animation] = cells.map((cell, index) => {
            const name = `${animation}-${index}-mapped${hash}`
            frames[name] = { ...template, frame: { ...template.frame, x: cell.column * mapping.cellWidth, y: cell.row * mapping.cellHeight } }
            return name
        })
    }
    if (!Object.keys(frames).length) throw new Error("Keep at least one frame in the atlas")
    return { ...atlas, frames, animations }
}
